// core/services/ownerService.ts — typed client for /api/owner/* (reads + real write actions).
import { api } from '@/core/lib/apiClient';
import type {
  OwnerDashboard, OwnerUserRow,
  OwnerPostRow, ModerationOverview, MediaSummary, HashtagRow,
  AnalyticsPayload, HealthReport, OwnerAuditRow, AppealRow,
  OwnerCouncilTeams, TopicRefinerState, TopicRefinerRunResult,
  LinkPreviewState,
} from '@/core/models';

export const ownerService = {
  getDashboard(rangeDays = 7): Promise<OwnerDashboard> {
    return api<OwnerDashboard>(`/owner/dashboard/?range=${rangeDays}`);
  },
  getUsers(q = '', verified = '', status = ''): Promise<{ results: OwnerUserRow[]; count: number }> {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    if (verified) params.set('verified', verified);
    if (status) params.set('status', status);
    const qs = params.toString();
    return api(`/owner/users/${qs ? `?${qs}` : ''}`);
  },
  getModerationOverview: (): Promise<ModerationOverview> => api('/owner/moderation/overview/'),
  getMediaSummary: (): Promise<MediaSummary> => api('/owner/media/summary/'),
  getPosts: (): Promise<{ results: OwnerPostRow[] }> => api('/owner/posts/'),
  getHashtags: (): Promise<{ results: HashtagRow[] }> => api('/owner/hashtags/'),
  getAnalytics: (kind: string, rangeDays = 7): Promise<AnalyticsPayload> =>
    api(`/owner/analytics/${kind}/?range=${rangeDays}`),
  getHealth: (): Promise<HealthReport> => api('/owner/health/'),
  getAudit: (): Promise<{ results: OwnerAuditRow[] }> => api('/owner/audit/'),

  // ── Account sanction actions ────────────────────────────────────────────────
  suspend: (username: string, category: string, note?: string) =>
    api('/owner/actions/suspend', { method: 'POST', body: { username, category, note } }),
  unsuspend: (username: string) =>
    api('/owner/actions/unsuspend', { method: 'POST', body: { username } }),
  ban: (username: string, category: string, note?: string) =>
    api('/owner/actions/ban', { method: 'POST', body: { username, category, note } }),
  unban: (username: string) =>
    api('/owner/actions/unban', { method: 'POST', body: { username } }),

  // ── Verify / staff toggles ──────────────────────────────────────────────────
  setVerified: (username: string, value: boolean) =>
    api(`/owner/actions/${value ? 'verify' : 'unverify'}`, { method: 'POST', body: { username } }),
  setStaff: (username: string, value: boolean) =>
    api(`/owner/actions/${value ? 'grant-staff' : 'revoke-staff'}`, { method: 'POST', body: { username } }),
  setCouncilModerator: (username: string, value: boolean) =>
    api(`/owner/actions/${value ? 'grant' : 'revoke'}-council-moderator`, {
      method: 'POST',
      body: { username },
    }),

  // ── Council governance offices ───────────────────────────────────────────────
  // Superuser-only server-side, and the backend refuses a self-action — so the
  // first President is seated by the seat_council_office management command,
  // not from here. Holding an office is separate from sitting in the moderator
  // pool: these never touch setCouncilModerator.
  setCouncilPresident: (username: string, value: boolean) =>
    api(`/owner/actions/${value ? 'grant' : 'revoke'}-council-president`, {
      method: 'POST',
      body: { username },
    }),
  setCouncilVicePresident: (username: string, value: boolean) =>
    api(`/owner/actions/${value ? 'grant' : 'revoke'}-council-vice-president`, {
      method: 'POST',
      body: { username },
    }),
  setCouncilSenator: (username: string, value: boolean) =>
    api(`/owner/actions/${value ? 'grant' : 'revoke'}-council-senator`, {
      method: 'POST',
      body: { username },
    }),

  // ── Council teams / head-moderator appointment ───────────────────────────────
  // The read carries a per-member `eligibility` code computed by the same
  // function the two writes run, so the console can state a refusal before the
  // operator clicks rather than after. Refetch after every write: a sanction or
  // a Council decision strips a headship, and a cached list would go on offering
  // a control that no longer applies.
  //
  // Grant takes teamId + username; revoke takes teamId ONLY — standing a head
  // down has no target account, so these are two methods rather than one
  // value-toggled slug like the office setters.
  getCouncilTeams: (): Promise<OwnerCouncilTeams> => api('/owner/council/teams/'),
  appointHeadModerator: (teamId: string, username: string) =>
    api('/owner/actions/grant-head-moderator-appointment', {
      method: 'POST',
      body: { teamId, username },
    }),
  revokeHeadModerator: (teamId: string) =>
    api('/owner/actions/revoke-head-moderator-appointment', {
      method: 'POST',
      body: { teamId },
    }),

  // ── Post removal ────────────────────────────────────────────────────────────
  removePost: (postId: string, category: string, note?: string) =>
    api('/owner/actions/remove-post', { method: 'POST', body: { postId, category, note } }),
  restorePost: (postId: string) =>
    api('/owner/actions/restore-post', { method: 'POST', body: { postId } }),

  // ── Topic refiner (1.24.00 B4) ───────────────────────────────────────────────
  // The read has a trailing slash (every owner read does); the three writes are
  // `POST /owner/actions/<slug>` with an empty body, superuser-only server-side.
  // `enable` and `run` answer 409 {code:'not_configured'} when ANTHROPIC_API_KEY
  // is blank — a switch that can't reach anything must not flip. Each write is
  // audited under its own string-literal slug (owner slug guard counts them).
  getTopicRefiner: (): Promise<TopicRefinerState> => api('/owner/topic-refiner/'),
  enableTopicRefiner: () =>
    api('/owner/actions/enable-topic-refiner', { method: 'POST', body: {} }),
  disableTopicRefiner: () =>
    api('/owner/actions/disable-topic-refiner', { method: 'POST', body: {} }),
  /**
   * Runs ONE batch synchronously (up to the server's LLM timeout) and returns
   * the fresh aggregate PLUS `ran` — see TopicRefinerRunResult. Only this one
   * of the three carries the flag; enable/disable answer the bare aggregate.
   */
  runTopicRefiner: (): Promise<TopicRefinerRunResult> =>
    api('/owner/actions/run-topic-refiner', { method: 'POST', body: {} }),

  // ── Link previews (1.24.00 F1) ───────────────────────────────────────────────
  // The kill switch for outbound link-preview fetching. Read is trailing-slash
  // (every owner read is); the two writes are `POST /owner/actions/<slug>` with
  // an empty body, superuser-only server-side — pausing stops the platform
  // reaching third-party sites, which is not a staff-level call. Each write is
  // audited under its own string-literal slug and answers the refreshed
  // aggregate, so the section can show the server's truth after a click.
  getLinkPreviews: (): Promise<LinkPreviewState> => api('/owner/link-previews/'),
  pauseLinkPreviews: (): Promise<LinkPreviewState> =>
    api('/owner/actions/pause-link-previews', { method: 'POST', body: {} }),
  resumeLinkPreviews: (): Promise<LinkPreviewState> =>
    api('/owner/actions/resume-link-previews', { method: 'POST', body: {} }),

  // ── Appeals ──────────────────────────────────────────────────────────────────
  getAppeals: (status = 'pending'): Promise<{ results: AppealRow[] }> =>
    api(`/owner/appeals?status=${status}`),
  reviewAppeal: (id: number, decision: 'approve' | 'reject') =>
    api(`/owner/appeals/${id}/${decision}`, { method: 'POST' }),
} as const;
